import React from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { HomeAPI } from '@/services/residents/api/HomeAPI';
import { Bulletin } from '@/types/residents/bulletin';
import { decrementUnreadBulletinsCount, markBulletinAsRead, setSelectedBulletinId } from '@/store/slices/residents/homeSlice';
import { formatDateMMMdd as formatMMMdd, formatDateMMMddyyyy as formatMMMddyyyy } from '@/utils/residents/date';
import { 
  useGetPendingFeedbackRequestsQuery, 
  useGetMarketplaceNotificationsQuery,
  useMarkMarketplaceNotificationAsReadMutation,
  useDeleteMarketplaceNotificationMutation,
  FeedbackRequest,
  MarketplaceNotification,
} from '@/store/api';
import {
  useGetCorkBoardNotificationsQuery,
  useMarkCorkBoardNotificationAsReadMutation,
  useDeleteCorkBoardNotificationMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { NOTIFICATION_TYPES } from '@/constants/residents/notifications';
import { useUnreadPropertyChatCount } from '../residents/useUnreadPropertyChatCount';
import type { CorkBoardNotification } from '@/types/residents/corkBoard';

// Extended Bulletin type to support marketplace feedback requests, notifications, property chat, and cork board warnings
export interface NotificationItem extends Bulletin {
  notificationType?: 'bulletin' | 'marketplace_feedback' | 'marketplace_notification' | 'property_chat' | 'cork_board_warning';
  feedbackRequest?: FeedbackRequest;
  marketplaceNotification?: MarketplaceNotification;
  corkBoardNotification?: CorkBoardNotification;
  propertyChatCount?: number;
}

// ID prefix for marketplace feedback notifications (to avoid collision with bulletin IDs)
const MARKETPLACE_FEEDBACK_ID_PREFIX = -1000000;
// ID prefix for marketplace received notifications
const MARKETPLACE_NOTIFICATION_ID_PREFIX = -2000000;
// ID for property chat summary notification (single fixed ID since there's only one)
const PROPERTY_CHAT_NOTIFICATION_ID = -3000000;
// ID prefix for cork board warning notifications
const CORK_BOARD_WARNING_ID_PREFIX = -4000000;

export type UseNotificationsResult = {
  notifications: NotificationItem[] | undefined;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  reorderForSelected: (list: NotificationItem[], selectedId?: number | null) => NotificationItem[];
  isUnread: (b: NotificationItem) => boolean;
  formatDateMMMdd: (dateStr: string) => string;
  formatDateMMMddyyyy: (dateStr: string) => string;
  markFirstNAsRead: (list: NotificationItem[], n: number) => Promise<number>;
  markAsRead: (notification: NotificationItem) => Promise<void>;
  deleteNotification: (bulletinId: number) => Promise<void>;
  isMarketplaceFeedback: (b: NotificationItem) => boolean;
  isMarketplaceNotification: (b: NotificationItem) => boolean;
  isPropertyChat: (b: NotificationItem) => boolean;
  isCorkBoardWarning: (b: NotificationItem) => boolean;
  getFeedbackRequest: (b: NotificationItem) => FeedbackRequest | undefined;
  getMarketplaceNotification: (b: NotificationItem) => MarketplaceNotification | undefined;
  getCorkBoardNotification: (b: NotificationItem) => CorkBoardNotification | undefined;
  markPropertyChatAsViewed: () => void;
};

/**
 * Transform a marketplace feedback request into a NotificationItem (Bulletin-like object)
 */
const transformFeedbackRequestToNotification = (request: FeedbackRequest): NotificationItem => {
  return {
    // Use a unique negative ID to avoid collision with bulletin IDs
    id: MARKETPLACE_FEEDBACK_ID_PREFIX - Number(request.id),
    title: `Leave feedback for ${request.seller.name}`,
    description: `You purchased "${request.listing.title}". Please leave a review for the seller.`,
    body: `You purchased "${request.listing.title}" from ${request.seller.name}. Please leave a review to help other community members.`,
    createdAt: request.createdAt,
    messageReadCount: '0', // Always unread until completed
    notificationType: 'marketplace_feedback',
    feedbackRequest: request,
  };
};

/**
 * Transform a marketplace notification into a NotificationItem (Bulletin-like object)
 */
const transformMarketplaceNotificationToNotification = (notification: MarketplaceNotification): NotificationItem => {
  // For received feedback, the title is the listing title and body is the review text
  const listingTitle = notification.metadata?.listingTitle || notification.message || '';
  const reviewText = notification.metadata?.reviewText || '';
  const buyerName = notification.metadata?.buyerName || 'Someone';
  
  return {
    // Use a unique negative ID to avoid collision with bulletin IDs
    id: MARKETPLACE_NOTIFICATION_ID_PREFIX - Number(notification.id),
    title: notification.title, // "You received feedback!"
    description: listingTitle, // Listing title for preview
    body: reviewText, // The actual review text
    createdAt: notification.createdAt,
    messageReadCount: notification.isRead ? '1' : '0',
    notificationType: 'marketplace_notification',
    marketplaceNotification: notification,
  };
};

const transformCorkBoardWarningToNotification = (notification: CorkBoardNotification): NotificationItem => {
  return {
    id: CORK_BOARD_WARNING_ID_PREFIX - Number(notification.id),
    title: notification.title,
    description: notification.message,
    body: notification.message,
    createdAt: notification.createdAt,
    messageReadCount: notification.isRead ? '1' : '0',
    notificationType: 'cork_board_warning',
    corkBoardNotification: notification,
  };
};

export const useNotifications = (): UseNotificationsResult => {
  const dispatch = useAppDispatch();
  const selectedId = useAppSelector((s) => s.home.selectedBulletinId || null);

  const [bulletins, setBulletins] = React.useState<Bulletin[] | undefined>(undefined);
  const [isLoadingBulletins, setIsLoadingBulletins] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isMountedRef = React.useRef(true);

  // Get property chat unread count
  const propertyChatCount = useUnreadPropertyChatCount();
  
  // Track if property chat notification has been viewed (tapped) for optimistic UI update
  const [propertyChatViewed, setPropertyChatViewed] = React.useState(false);
  
  // Reset viewed state when new unread messages arrive (count increases)
  const prevPropertyChatCountRef = React.useRef(propertyChatCount);
  React.useEffect(() => {
    if (propertyChatCount > prevPropertyChatCountRef.current) {
      // New unread messages arrived, reset viewed state
      setPropertyChatViewed(false);
    }
    prevPropertyChatCountRef.current = propertyChatCount;
  }, [propertyChatCount]);

  // Fetch pending marketplace feedback requests using RTK Query
  const { 
    data: pendingFeedbackRequests = [], 
    isLoading: isLoadingFeedback,
    refetch: refetchFeedback,
  } = useGetPendingFeedbackRequestsQuery(undefined, {
    // Don't poll here - let the count hook handle polling
    refetchOnMountOrArgChange: true,
  });

  // Fetch marketplace notifications (e.g., "You received feedback")
  const {
    data: marketplaceNotifications = [],
    isLoading: isLoadingMarketplaceNotifications,
    refetch: refetchMarketplaceNotifications,
  } = useGetMarketplaceNotificationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Fetch cork board notifications (warnings, etc.)
  const {
    data: corkBoardNotifications = [],
    isLoading: isLoadingCorkBoardNotifications,
    refetch: refetchCorkBoardNotifications,
  } = useGetCorkBoardNotificationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Mutations for marketplace notifications
  const [markNotificationAsRead] = useMarkMarketplaceNotificationAsReadMutation();
  const [deleteMarketplaceNotification] = useDeleteMarketplaceNotificationMutation();

  // Mutations for cork board notifications
  const [markCorkBoardNotificationAsRead] = useMarkCorkBoardNotificationAsReadMutation();
  const [deleteCorkBoardNotification] = useDeleteCorkBoardNotificationMutation();

  // Check if notification is a marketplace feedback request
  const isMarketplaceFeedback = React.useCallback((b: NotificationItem): boolean => {
    return b.notificationType === 'marketplace_feedback';
  }, []);

  // Check if notification is a marketplace notification (received feedback, etc.)
  const isMarketplaceNotification = React.useCallback((b: NotificationItem): boolean => {
    return b.notificationType === 'marketplace_notification';
  }, []);

  // Check if notification is a property chat summary
  const isPropertyChat = React.useCallback((b: NotificationItem): boolean => {
    return b.notificationType === 'property_chat';
  }, []);

  // Get the original feedback request from a notification item
  const getFeedbackRequest = React.useCallback((b: NotificationItem): FeedbackRequest | undefined => {
    return b.feedbackRequest;
  }, []);

  // Get the original marketplace notification from a notification item
  const getMarketplaceNotification = React.useCallback((b: NotificationItem): MarketplaceNotification | undefined => {
    return b.marketplaceNotification;
  }, []);

  const isCorkBoardWarning = React.useCallback((b: NotificationItem): boolean => {
    return b.notificationType === 'cork_board_warning';
  }, []);

  const getCorkBoardNotification = React.useCallback((b: NotificationItem): CorkBoardNotification | undefined => {
    return b.corkBoardNotification;
  }, []);

  const isUnread = React.useCallback((b: NotificationItem) => {
    if (b.notificationType === 'marketplace_feedback') {
      return true;
    }
    if (b.notificationType === 'marketplace_notification' && b.marketplaceNotification) {
      return !b.marketplaceNotification.isRead;
    }
    if (b.notificationType === 'property_chat') {
      return !propertyChatViewed;
    }
    if (b.notificationType === 'cork_board_warning' && b.corkBoardNotification) {
      return !b.corkBoardNotification.isRead;
    }
    return !b.messageReadCount || b.messageReadCount === '0';
  }, [propertyChatViewed]);

  const reorderForSelected = React.useCallback((list: NotificationItem[], selected?: number | null) => {
    const sorted = [...(list || [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const isSel = (val: NotificationItem) => val.id === (selected ?? -1);
    const selItem = sorted.find(isSel);
    const unread = sorted.filter((n) => !isSel(n) && isUnread(n));
    const read = sorted.filter((n) => !isSel(n) && !isUnread(n));
    return selItem ? [selItem, ...unread, ...read] : [...unread, ...read];
  }, [isUnread]);

  const initialSelectedIdRef = React.useRef<number | null | undefined>(selectedId);

  // Combine bulletins, feedback requests, marketplace notifications, and property chat into a single list
  const notifications = React.useMemo((): NotificationItem[] | undefined => {
    if (bulletins === undefined) return undefined;
    
    // Transform feedback requests to notification items
    const feedbackNotifications: NotificationItem[] = pendingFeedbackRequests.map(
      transformFeedbackRequestToNotification
    );
    
    // Transform marketplace notifications to notification items
    // Filter out "leave_feedback" type since we show those as pending feedback requests
    const marketplaceNotificationItems: NotificationItem[] = marketplaceNotifications
      .filter(n => n.type === NOTIFICATION_TYPES.marketplaceReceivedFeedback.key)
      .map(transformMarketplaceNotificationToNotification);
    
    // Mark bulletins with their type
    const bulletinNotifications: NotificationItem[] = bulletins.map(b => ({
      ...b,
      notificationType: 'bulletin' as const,
    }));
    
    // Transform cork board notifications to notification items
    const corkBoardWarningItems: NotificationItem[] = corkBoardNotifications
      .filter(n => n.type === NOTIFICATION_TYPES.corkBoardWarning.key)
      .map(transformCorkBoardWarningToNotification);

    // Create property chat summary notification if there are unread messages
    const propertyChatNotification: NotificationItem[] = propertyChatCount > 0 ? [{
      id: PROPERTY_CHAT_NOTIFICATION_ID,
      title: 'Unread Messages',
      description: propertyChatCount === 1 
        ? 'You have 1 unread conversation' 
        : `You have ${propertyChatCount} unread conversations`,
      body: 'Tap to view your messages',
      createdAt: new Date().toISOString(),
      messageReadCount: '0',
      notificationType: 'property_chat' as const,
      propertyChatCount,
    }] : [];
    
    // Combine and reorder
    const combined = [...bulletinNotifications, ...feedbackNotifications, ...marketplaceNotificationItems, ...corkBoardWarningItems, ...propertyChatNotification];
    return reorderForSelected(combined, initialSelectedIdRef.current);
  }, [bulletins, pendingFeedbackRequests, marketplaceNotifications, corkBoardNotifications, propertyChatCount, reorderForSelected]);

  const isLoading = isLoadingBulletins || isLoadingFeedback || isLoadingMarketplaceNotifications || isLoadingCorkBoardNotifications;

  const load = React.useCallback(async () => {
    setIsLoadingBulletins(true);
    setError(null);
    try {
      const list = await HomeAPI.getNotifications();
      if (isMountedRef.current) {
        setBulletins(list);
      }
    } catch (e) {
      if (isMountedRef.current) {
        setBulletins([]);
        setError('Failed to load notifications');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingBulletins(false);
      }
    }
  }, []);

  // Combined reload function
  const reload = React.useCallback(async () => {
    await Promise.all([
      load(),
      refetchFeedback(),
      refetchMarketplaceNotifications(),
      refetchCorkBoardNotifications(),
    ]);
  }, [load, refetchFeedback, refetchMarketplaceNotifications, refetchCorkBoardNotifications]);

  React.useEffect(() => {
    load();
    return () => {
      isMountedRef.current = false;
    };
  }, [load]);

  const markFirstNAsRead = React.useCallback(async (list: NotificationItem[], n: number) => {
    // Only mark bulletins as read - marketplace notifications are handled separately
    const unreadBulletins = list
      .filter((b) => b.notificationType === 'bulletin' && isUnread(b))
      .slice(0, Math.max(0, n));
    
    // Handle marketplace notifications separately
    const unreadMarketplaceNotifications = list
      .filter((b) => b.notificationType === 'marketplace_notification' && isUnread(b))
      .slice(0, Math.max(0, n));
    
    if (unreadBulletins.length === 0 && unreadMarketplaceNotifications.length === 0) return 0;
    
    // Collect IDs of bulletins to mark as read
    const bulletinIdsToMark = new Set(unreadBulletins.map(b => b.id));
    
    // Mark bulletins as read
    await Promise.all(unreadBulletins.map(async (b) => {
      try {
        await HomeAPI.markBulletinAsRead(b.id);
        dispatch(markBulletinAsRead(b.id));
      } catch {
        // Remove from set if API call failed
        bulletinIdsToMark.delete(b.id);
      }
    }));
    
    // Update local bulletins state to reflect read status
    if (bulletinIdsToMark.size > 0 && isMountedRef.current) {
      setBulletins((prev) => 
        prev?.map((b) => 
          bulletinIdsToMark.has(b.id) 
            ? { ...b, messageReadCount: '1' } 
            : b
        )
      );
    }
    
    // Mark marketplace notifications as read using the correct API
    await Promise.all(unreadMarketplaceNotifications.map(async (b) => {
      try {
        if (b.marketplaceNotification) {
          await markNotificationAsRead(String(b.marketplaceNotification.id)).unwrap();
        }
      } catch {}
    }));
    
    dispatch(decrementUnreadBulletinsCount(unreadBulletins.length));
    return unreadBulletins.length + unreadMarketplaceNotifications.length;
  }, [dispatch, isUnread, markNotificationAsRead]);

  /**
   * Mark a single notification as read.
   * Handles all notification types (bulletin, marketplace notification, property chat).
   * Marketplace feedback requests are skipped since they're "completed" not "read".
   */
  const markAsRead = React.useCallback(async (notification: NotificationItem): Promise<void> => {
    if (isMarketplaceFeedback(notification)) return;

    if (isMarketplaceNotification(notification) && isUnread(notification)) {
      const marketplaceNotif = getMarketplaceNotification(notification);
      if (marketplaceNotif) {
        await markNotificationAsRead(String(marketplaceNotif.id)).unwrap();
      }
      return;
    }

    if (isCorkBoardWarning(notification) && isUnread(notification)) {
      const cbNotif = getCorkBoardNotification(notification);
      if (cbNotif) {
        await markCorkBoardNotificationAsRead(cbNotif.id).unwrap();
      }
      return;
    }

    if (notification.notificationType === 'bulletin' && isUnread(notification)) {
      await HomeAPI.markBulletinAsRead(notification.id);
      dispatch(markBulletinAsRead(notification.id));
      dispatch(decrementUnreadBulletinsCount(1));
      
      if (isMountedRef.current) {
        setBulletins((prev) =>
          prev?.map((b) =>
            b.id === notification.id ? { ...b, messageReadCount: '1' } : b
          )
        );
      }
    }
  }, [dispatch, isUnread, isMarketplaceFeedback, isMarketplaceNotification, getMarketplaceNotification, markNotificationAsRead, isCorkBoardWarning, getCorkBoardNotification, markCorkBoardNotificationAsRead]);

  // Re-export date formatting utilities for convenience
  // Note: Components can also import these directly from '@/utils/residents/date'
  const formatDateMMMdd = formatMMMdd;
  const formatDateMMMddyyyy = formatMMMddyyyy;

  const deleteNotification = React.useCallback(async (notificationId: number) => {
    if (notificationId === PROPERTY_CHAT_NOTIFICATION_ID) {
      return;
    }
    
    if (notificationId <= MARKETPLACE_FEEDBACK_ID_PREFIX && notificationId > MARKETPLACE_NOTIFICATION_ID_PREFIX) {
      return;
    }
    
    if (notificationId <= MARKETPLACE_NOTIFICATION_ID_PREFIX && notificationId > PROPERTY_CHAT_NOTIFICATION_ID) {
      const originalId = String(MARKETPLACE_NOTIFICATION_ID_PREFIX - notificationId);
      try {
        await deleteMarketplaceNotification(originalId).unwrap();
      } catch (e) {
        throw new Error('Failed to delete notification');
      }
      return;
    }

    if (notificationId <= CORK_BOARD_WARNING_ID_PREFIX) {
      const originalId = String(CORK_BOARD_WARNING_ID_PREFIX - notificationId);
      try {
        await deleteCorkBoardNotification(originalId).unwrap();
      } catch (e) {
        throw new Error('Failed to delete notification');
      }
      return;
    }
    
    try {
      await HomeAPI.deleteBulletin(notificationId);
      if (isMountedRef.current) {
        setBulletins((prev) => prev?.filter((n) => n.id !== notificationId));
      }
    } catch (e) {
      throw new Error('Failed to delete notification');
    }
  }, [deleteMarketplaceNotification, deleteCorkBoardNotification]);

  // Mark property chat notification as viewed (optimistic UI update)
  const markPropertyChatAsViewed = React.useCallback(() => {
    setPropertyChatViewed(true);
  }, []);

  return {
    notifications,
    isLoading,
    error,
    reload,
    reorderForSelected,
    isUnread,
    formatDateMMMdd,
    formatDateMMMddyyyy,
    markFirstNAsRead,
    markAsRead,
    deleteNotification,
    isMarketplaceFeedback,
    isMarketplaceNotification,
    isPropertyChat,
    isCorkBoardWarning,
    getFeedbackRequest,
    getMarketplaceNotification,
    getCorkBoardNotification,
    markPropertyChatAsViewed,
  };
};


